import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { OverrideEditor } from './override-editor';

// Per-number rows: each WhatsApp number can override the defaults. One row per gateway from
// the payload's catalog, plus any settings.numbers key the catalog no longer knows (a
// renamed or retired gateway) so a stored override never becomes uneditable — those render
// with an "unknown number" chip. A number with no stored row simply "Uses defaults".
// Expansion is inline (one number at a time).

const EMPTY_ROW = Object.freeze({
  enabled: null,
  timezone: null,
  weekly: null,
  closeOnPublicHolidays: null,
  mode: null,
  message: null,
});

const pretty = (zone) => String(zone || '').replace(/_/g, ' ');

function hasOverride(row) {
  return Boolean(
    row &&
      (row.enabled !== null ||
        row.timezone !== null ||
        row.weekly !== null ||
        row.closeOnPublicHolidays !== null ||
        row.mode !== null ||
        row.message !== null),
  );
}

function rowSummary(row) {
  if (!hasOverride(row)) return 'Uses the default hours and reply';
  const bits = [];
  if (row.enabled === false) bits.push('gate off');
  else if (row.enabled === true) bits.push('gate on');
  if (row.timezone) bits.push(pretty(row.timezone));
  if (row.weekly) bits.push('own hours');
  if (row.closeOnPublicHolidays === true) bits.push('closed on holidays');
  else if (row.closeOnPublicHolidays === false) bits.push('open on holidays');
  if (row.mode) bits.push(row.mode === 'silent' ? 'silent' : 'reply');
  else if (row.message) bits.push('own message');
  return bits.join(' · ');
}

export function NumberOverridesSection({
  gateways = [],
  value = {},
  inherited,
  placeholders = [],
  errors = {},
  disabled = false,
  onSetNumber,
}) {
  const [openId, setOpenId] = useState(null);

  const known = new Set(gateways.map((gateway) => gateway.botId));
  const rows = [
    ...gateways.map((gateway) => ({ botId: gateway.botId, label: gateway.label, unknown: false })),
    ...Object.keys(value)
      .filter((botId) => !known.has(botId))
      .sort()
      .map((botId) => ({ botId, label: botId, unknown: true })),
  ];

  if (rows.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-xs text-muted-foreground">
        No WhatsApp numbers are configured on this server.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {rows.map((row) => {
        const override = value[row.botId] || null;
        const customized = hasOverride(override);
        const open = openId === row.botId;
        return (
          <Collapsible
            key={row.botId}
            open={open}
            onOpenChange={(next) => setOpenId(next ? row.botId : null)}
          >
            <div className="rounded-lg border border-border bg-card">
              <button
                type="button"
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-expanded={open}
                onClick={() => setOpenId(open ? null : row.botId)}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'truncate text-[13px] font-semibold text-foreground',
                        row.unknown && 'font-mono',
                      )}
                    >
                      {row.label}
                    </span>
                    {row.unknown ? (
                      <span
                        title="This number has a stored override but is missing from the gateway catalog — it may have been renamed or retired."
                        className="shrink-0 rounded-full border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] text-muted-foreground"
                      >
                        unknown number
                      </span>
                    ) : (
                      <span className="shrink-0 truncate font-mono text-[11px] text-muted-foreground">
                        {row.botId}
                      </span>
                    )}
                  </div>
                  <p className="truncate text-[11px] text-muted-foreground">{rowSummary(override)}</p>
                </div>
                {customized ? (
                  <Badge variant="outline" className="shrink-0 border-primary/40 bg-primary/10 text-primary">
                    Customized
                  </Badge>
                ) : (
                  <Badge variant="outline" className="shrink-0 text-muted-foreground">
                    Uses defaults
                  </Badge>
                )}
                <ChevronDown
                  className={cn(
                    'size-4 shrink-0 text-muted-foreground transition-transform',
                    open && 'rotate-180',
                  )}
                  aria-hidden
                />
              </button>
              <CollapsibleContent>
                <div className="flex flex-col gap-3 border-t border-border px-3 py-3">
                  <OverrideEditor
                    value={override || EMPTY_ROW}
                    inherited={inherited}
                    placeholders={placeholders}
                    errors={errors}
                    errorPrefix={`numbers.${row.botId}`}
                    disabled={disabled}
                    onChange={(patch) => onSetNumber(row.botId, patch)}
                  />
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        );
      })}
    </div>
  );
}
